import React, { ComponentProps } from "react";
import { Button, Stack, SvgIcon, Typography } from "@mui/material";

import { ReactComponent as SomethingWentWrongSvg } from "@/ui/assets/vectors/something_went_wrong.svg";

type SomethingWentWrongSlotProps = {
  root?: ComponentProps<typeof Stack>;
  icon?: ComponentProps<typeof SvgIcon>;
  title?: ComponentProps<typeof Typography>;
  message?: ComponentProps<typeof Typography>;
  button?: ComponentProps<typeof Button>;
};

type SomethingWentWrongProps = {
  /** Optional callback for the retry button. Defaults to reloading the page. Pass null to hide the button. */
  onRetry?: (() => void) | null;
  /** The size of the component. */
  size?: "regular" | "compact";
  /** Custom title text. */
  title?: string;
  /** Custom message text. */
  message?: string;
  /** Custom button label. */
  buttonLabel?: string;
  /** Props to extend each internal slot. */
  slotProps?: SomethingWentWrongSlotProps;
};

/**
 * A component to display when an error occurs, showing an illustration, a message, and a retry button.
 */
export const SomethingWentWrong: React.FC<SomethingWentWrongProps> = ({
  onRetry,
  size = "regular",
  title = "Uh-oh, Something Went Wrong",
  message = "We're experiencing technical difficulties. Our team is working on it. Please try again later.",
  buttonLabel = "Retry",
  slotProps = {},
}) => {
  const refreshPage = () => {
    window.location.reload();
  };

  const titleVariant = size === "compact" ? "subtitle1" : "h6";
  const messageVariant = size === "compact" ? "body2" : "body1";
  return (
    <Stack
      justifyContent="center"
      textAlign="center"
      alignItems="center"
      height="100%"
      width="100%"
      {...slotProps.root}
    >
      <SvgIcon
        component={SomethingWentWrongSvg}
        inheritViewBox
        {...slotProps.icon}
        sx={{
          width: size === "compact" ? 60 : 120,
          height: size === "compact" ? 60 : 120,
          ...slotProps.icon?.sx,
        }}
      />
      <Typography variant={titleVariant} {...slotProps.title}>
        {title}
      </Typography>
      <Typography
        variant={messageVariant}
        color="text.secondary"
        {...slotProps.message}
      >
        {message}
      </Typography>
      {onRetry !== null && (
        <Button
          size={size === "compact" ? "medium" : "large"}
          variant="contained"
          onClick={onRetry ?? refreshPage}
          {...slotProps.button}
          sx={{ mt: 2, ...slotProps.button?.sx }}
        >
          {buttonLabel}
        </Button>
      )}
    </Stack>
  );
};

export default SomethingWentWrong;
